import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import moment from 'moment-timezone';

import { AdminsSelectDialogComponent } from '@core/account-smart-ui';
import type { User } from '@core/account-types';
import { filterDefined } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { OfferSelectOption } from '@core/offers-ui';
import type { TimeData } from '@core/time-ui';
import {
  DialogService,
  DialogWidth,
  DividerComponent,
  DropdownComponent,
  DropdownWidth,
  HeadingComponent,
} from '@core/user-interface-legacy';
import { SubscriptionService } from '@recruitee/company-logic';
import {
  RequestEvaluationDataService,
  RequestEvaluationDropdownFormService,
} from '@recruitee/evaluations-logic';
import { RequestEvaluationDropdownContext } from '@recruitee/evaluations-types';
import type {
  EvaluationDueDateConfig,
  EvaluationSendAtKind,
  RequestEvaluationSubmit,
} from '@recruitee/evaluations-types';
import {
  EvaluationDropdownEmptyStateComponent,
  EvaluationDropdownHeaderComponent,
  RequestEvaluationDropdownFooterComponent,
} from '@recruitee/evaluations-ui';
import { FeatureDiscoveryBannerComponent } from '@recruitee/feature-discovery-smart-ui';

import { RequestEvaluationDropdownFormComponent } from './request-evaluation-dropdown-form/request-evaluation-dropdown-form.component';

@Component({
  selector: 'rt-request-evaluation-dropdown',
  templateUrl: './request-evaluation-dropdown.component.html',
  styleUrls: ['./request-evaluation-dropdown.component.less'],
  imports: [
    DropdownComponent,
    TranslatePipe,
    EvaluationDropdownHeaderComponent,
    EvaluationDropdownEmptyStateComponent,
    RequestEvaluationDropdownFooterComponent,
    RequestEvaluationDropdownFormComponent,
    DividerComponent,
    FeatureDiscoveryBannerComponent,
    HeadingComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [RequestEvaluationDropdownFormService, RequestEvaluationDataService],
})
export class RequestEvaluationDropdownComponent implements OnInit {
  private readonly requestEvaluationDataService = inject(RequestEvaluationDataService);
  private readonly requestEvaluationDropdownFormService = inject(
    RequestEvaluationDropdownFormService,
  );
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly subscriptionService = inject(SubscriptionService);

  private readonly offerId = toSignal(this.requestEvaluationDropdownFormService.offerId$);
  private readonly syncCurrentOffer = effect(() =>
    this.requestEvaluationDataService.currentOffer.set(this.currentOffer()),
  );
  private readonly syncCandidateId = effect(() =>
    this.requestEvaluationDataService.candidateId.set(this.candidateId()),
  );
  private readonly preselectTemplateId = effect(() => {
    const preselectedTemplateId = this.preselectedTemplateId();

    if (preselectedTemplateId !== null && !this.form.controls.templateId.value) {
      this.requestEvaluationDropdownFormService.setTemplateId(preselectedTemplateId);
    }
  });
  private readonly pruneAdminsOnAccessChange = effect(() => {
    const groupedUsers = this.requestEvaluationDataService.groupedUsers();
    const isDefaultContext = this.context() === RequestEvaluationDropdownContext.Default;

    if (groupedUsers && isDefaultContext) {
      this.requestEvaluationDropdownFormService.pruneAdminsByAccess(groupedUsers.usersWithAccess);
    }
  });

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly canManageInterviewTemplates =
    this.requestEvaluationDataService.canManageInterviewTemplates;
  protected readonly canViewCandidates = this.requestEvaluationDataService.canViewCandidates;
  protected readonly allGroupedEvaluationForms =
    this.requestEvaluationDataService.allGroupedEvaluationForms;
  protected readonly form = this.requestEvaluationDropdownFormService.form;
  protected readonly currentOffer = computed(() =>
    this.offers().find(offer => offer.id === this.offerId()),
  );
  protected readonly visibleTemplates = this.requestEvaluationDataService.visibleTemplates;
  protected readonly templatesLoaded = this.requestEvaluationDataService.templatesLoaded;
  protected readonly preselectedTemplateId =
    this.requestEvaluationDataService.preselectedTemplateId;
  protected readonly allowedUsers = this.requestEvaluationDataService.allowedUsers;
  protected readonly disallowedUsers = this.requestEvaluationDataService.disallowedUsers;
  protected readonly isFormValid = this.requestEvaluationDropdownFormService.isValid;
  protected readonly timeData = signal<TimeData | null>(null);
  protected readonly dueDateConfig = signal<EvaluationDueDateConfig | null>(null);
  protected readonly sendAtKind = signal<EvaluationSendAtKind>('on_day_of_event');

  public readonly context = input<RequestEvaluationDropdownContext>(
    RequestEvaluationDropdownContext.Default,
  );
  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly candidateId = input<number>();
  public readonly preselectedAdmins = input<User[]>([]);
  public readonly preselectedOfferId = input<number>();
  public readonly isSchedulerLink = input<boolean>(false);

  public close = output<void>();
  public submit = output<RequestEvaluationSubmit>();

  public ngOnInit(): void {
    const [firstOffer] = this.offers();
    const preSelectedOfferId = this.preselectedOfferId();
    const preselectedAdmins = this.preselectedAdmins();

    if (firstOffer || preSelectedOfferId) {
      this.requestEvaluationDropdownFormService.setOfferId(
        preSelectedOfferId || Number(firstOffer.id),
      );
      this.cdRef.detectChanges();
    }

    if (preselectedAdmins.length) {
      this.requestEvaluationDropdownFormService.setAdmins(preselectedAdmins);
      this.cdRef.detectChanges();
    }
  }

  protected get shouldShowScaleBanner(): boolean {
    return !this.subscriptionService.isTrial();
  }

  protected openEvaluationScaleSettings(): void {
    const urlTree = this.router.createUrlTree(['/settings/candidate_evaluation'], {
      queryParams: { edit: 'scale' },
    });
    window.open(this.location.prepareExternalUrl(this.router.serializeUrl(urlTree)), '_blank');
  }

  protected onEditAssignments(): void {
    const data = {
      assignments: this.form.controls.admins.value.map(admin => admin.id),
      heading: this.i18n.translate('app.interview.schedule_dialog.assignments_title'),
      subheading: this.i18n.translate('app.interview.schedule_dialog.assignments_desc'),
      allowedUsers: this.allowedUsers(),
      disallowedUsers: this.disallowedUsers(),
    };

    this.dialogService
      .openCustom(
        AdminsSelectDialogComponent,
        { width: DialogWidth.SmallX },
        data,
        this.viewContainerRef,
      )
      .afterClosed.pipe(filterDefined)
      .subscribe(({ users }) => {
        this.requestEvaluationDropdownFormService.setAdmins(users);
        this.cdRef.detectChanges();
      });
  }

  protected onClose() {
    this.close.emit();
  }

  protected onSubmit(): void {
    const offerId = this.form.controls.offerId.value ?? undefined;
    const isScheduler = this.context() === RequestEvaluationDropdownContext.Scheduler;

    this.submit.emit({
      templateId: this.form.controls.templateId.value!,
      admins: this.form.controls.admins.value,
      ...(isScheduler
        ? { dueDateConfig: this.dueDateConfig(), sendAtKind: this.sendAtKind() }
        : { dueDate: this.getDueDate() }),
      ...(this.requestEvaluationDataService.isTalentPool()
        ? { talentPoolId: offerId }
        : { offerId }),
    });
  }

  private getDueDate(): string | null {
    const timeData = this.timeData();

    if (!timeData) {
      return null;
    }

    return moment
      .tz(`${timeData.date}T${timeData.time}`, 'YYYY-MM-DDTHH:mm', timeData.zone)
      .utc()
      .format();
  }
}
